/**
 * Bidi isolates (docs/DESIGN.md §7.9). A number that is *formatted* as a compound — a duration, a
 * range, a ratio, a decimal — is one object, not two numbers that happen to sit near each other:
 * `4:15` is `4` and `15` around a separator, and the separator is a neutral. Inside a
 * right-to-left paragraph that neutral belongs to the surrounding direction, so the value is laid
 * out from the RTL end and the pair can trade places (the native POC's `4:15` renders as `١٥:٤`).
 *
 * The fix is one concept on every platform: an isolate. On the web it is `dir="ltr"` (the voice
 * transport, §7.6) or the Unicode isolate characters below; this module owns the second form, so a
 * formatted value is a closed unit *wherever it ends up* — in a JSX node, in an interpolation into
 * a sentence, or in a push notification body.
 *
 * `LRI`/`PDI` are zero-width and invisible; they only affect ordering. Screen readers skip them.
 */
export const LRI = '\u2066' // LEFT-TO-RIGHT ISOLATE
export const FSI = '\u2068' // FIRST-STRONG ISOLATE
export const PDI = '\u2069' // POP DIRECTIONAL ISOLATE

/** True when `value` is already wrapped in this module's LTR isolate characters. */
export function isIsolatedLtr(value: string): boolean {
  return value.length >= 2 && value.startsWith(LRI) && value.endsWith(PDI)
}

/**
 * Closes a **user string** as a unit whose direction is its own first strong character — the string
 * form of `<bdi>`, for a value that travels through `t()`'s `{placeholder}` substitution instead of
 * through markup: the query inside "3 results for “…”", the same query inside the empty state.
 *
 * A sentence is chrome and keeps the chrome's direction; the user string inside it must not drag
 * the words around it, which is exactly what an unisolated Arabic query does inside an English
 * sentence (and vice versa in the Arabic chrome). `dir="auto"`/`<bdi>` answer the same question for
 * a node; this is the answer for a value glued into a sentence (docs/DESIGN.md §7.9 rules 2 and 9).
 * Idempotent, like `isolateLtr`.
 */
export function isolateAuto(value: string): string {
  if (value === '' || (value.length >= 2 && value.startsWith(FSI) && value.endsWith(PDI))) return value
  return `${FSI}${value}${PDI}`
}

/**
 * Closes `value` as a left-to-right unit. Idempotent: an already-isolated value is returned
 * unchanged, so a value can pass through two layers of code without being wrapped twice (which
 * would nest isolates and is legal but pointless, and this is how a double-isolate would be
 * noticed).
 */
export function isolateLtr(value: string): string {
  if (value === '' || isIsolatedLtr(value)) return value
  return `${LRI}${value}${PDI}`
}

/**
 * The rule as a predicate, used by the test that pins it: does `value` glue two numeric runs
 * together with nothing but neutral separators between them, *outside* any isolate? `4:15`,
 * `12/10/26` and `1.5` are compounds; `12 Oct 2026` and `5 min ago` are not, because a letter
 * between the numbers settles the direction on its own.
 *
 * Text inside an isolate is dropped before the test, so a value that has been closed by
 * `isolateLtr` passes — which is the whole point of the rule.
 *
 * `\p{Nd}` covers ASCII and Arabic-Indic digits alike, so a locale that formats numbers in Arabic
 * script is caught by the same test.
 */
export function hasUnisolatedCompound(value: string): boolean {
  const bare = value.replace(/\u2066[^\u2069]*\u2069/g, '')
  return /\p{Nd}+(?:\s*[:./\-\u2013\u2014]\s*\p{Nd}+)+/u.test(bare)
}


/**
 * The direction a string *is*, which is what `dir="auto"` resolves on the web and what UAX #9 P2/P3
 * asks for: the first strong character decides, and neutrals (spaces, punctuation, digits, emoji)
 * are skipped. `null` when the string holds no letter at all — then there is no direction to take,
 * and the caller should leave the layout's own alone.
 *
 * It exists because a JSX parent cannot ask the browser this question: a block whose direction must
 * be decided by *its content's* first letter — a reply quote, whose own first line is an author name
 * ("You", the chrome's word) rather than the quoted text — cannot use `dir="auto"` for itself. The
 * quote's author and its excerpt have to land on the same side of the bubble, and the side is the
 * excerpt's, which is the string this function is asked about.
 */
const RTL_STRONG = /[\p{Script=Arabic}\p{Script=Hebrew}\p{Script=Syriac}\p{Script=Thaana}]/u
const ANY_LETTER = /\p{L}/u

export function firstStrongDirection(value: string): 'rtl' | 'ltr' | null {
  for (const character of value) {
    if (RTL_STRONG.test(character)) return 'rtl'
    if (ANY_LETTER.test(character)) return 'ltr'
  }
  return null
}
